import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { 
  FaPlus, FaArrowLeft, FaEdit, FaTrash, FaSave, FaTimes, 
  FaStar, FaEye, FaEyeSlash, FaArrowUp, FaArrowDown,
  FaCheck, FaGift, FaCrown, FaSpinner
} from 'react-icons/fa';
import './GerenciarPlanos.css';

function GerenciarPlanos() {
  // Estados do formulário principal
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');
  const [features, setFeatures] = useState(['']);
  const [color, setColor] = useState('#209cd3');
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [displayOrder, setDisplayOrder] = useState(1);
  const [planType, setPlanType] = useState('premium'); // free, premium, enterprise
  
  // Estados de controle
  const [plans, setPlans] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const ref = collection(db, 'planos_assinatura');
      const q = query(ref, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(q);
      const plansList = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      setPlans(plansList);
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
      setMensagem('Erro ao carregar planos: ' + error.message);
    }
  };

  const resetForm = () => {
    setName('');
    setPrice('');
    setOriginalPrice('');
    setDuration('');
    setDescription('');
    setFeatures(['']);
    setColor('#209cd3');
    setIsHighlighted(false);
    setIsActive(true);
    setDisplayOrder(1);
    setPlanType('premium');
  };

  const addFeature = () => {
    setFeatures([...features, '']);
  };

  const removeFeature = (index) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures);
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  const calculateDiscount = () => {
    if (!price || !originalPrice) return 0;
    const discount = ((parseFloat(originalPrice) - parseFloat(price)) / parseFloat(originalPrice)) * 100;
    return Math.round(discount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensagem('');
    setLoading(true);

    try {
      // Validações
      if (!name.trim()) throw new Error('Nome do plano é obrigatório');
      if (!description.trim()) throw new Error('Descrição é obrigatória');
      if (features.filter(f => f.trim()).length === 0) throw new Error('Adicione pelo menos um recurso');

      const planData = {
        name: name.trim(),
        price: parseFloat(price) || 0,
        originalPrice: parseFloat(originalPrice) || null,
        duration,
        description: description.trim(),
        features: features.filter(f => f.trim()),
        color,
        isHighlighted,
        isActive,
        displayOrder: parseInt(displayOrder),
        planType,
        discount: calculateDiscount(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'planos_assinatura'), planData);

      resetForm();
      setMensagem('Plano cadastrado com sucesso!');
      fetchPlans();
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => setMensagem(''), 3000);
    } catch (error) {
      console.error('Erro ao cadastrar plano:', error);
      setMensagem('Erro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (plan) => {
    setEditPlan({
      ...plan,
      features: plan.features || [''],
      originalPrice: plan.originalPrice || '',
      color: plan.color || '#209cd3',
      isHighlighted: plan.isHighlighted || false,
      isActive: plan.isActive !== undefined ? plan.isActive : true,
      displayOrder: plan.displayOrder || 1,
      planType: plan.planType || 'premium'
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setMensagem('');
    setLoading(true);

    try {
      const planRef = doc(db, 'planos_assinatura', editPlan.id);
      
      const updateData = {
        name: editPlan.name.trim(),
        price: parseFloat(editPlan.price) || 0,
        originalPrice: parseFloat(editPlan.originalPrice) || null,
        duration: editPlan.duration,
        description: editPlan.description.trim(),
        features: editPlan.features.filter(f => f.trim()),
        color: editPlan.color,
        isHighlighted: editPlan.isHighlighted,
        isActive: editPlan.isActive,
        displayOrder: parseInt(editPlan.displayOrder),
        planType: editPlan.planType,
        discount: editPlan.originalPrice ? 
          Math.round(((parseFloat(editPlan.originalPrice) - parseFloat(editPlan.price)) / parseFloat(editPlan.originalPrice)) * 100) : 0,
        updatedAt: Timestamp.now()
      };

      await updateDoc(planRef, updateData);
      
      setMensagem('Plano atualizado com sucesso!');
      setEditModalOpen(false);
      setEditPlan(null);
      fetchPlans();
      setTimeout(() => setMensagem(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      setMensagem('Erro ao atualizar plano: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza de que deseja deletar este plano?')) {
      return;
    }

    setMensagem('');
    try {
      const planDoc = doc(db, 'planos_assinatura', id);
      await deleteDoc(planDoc);
      setMensagem('Plano deletado com sucesso!');
      fetchPlans();
      setTimeout(() => setMensagem(''), 3000);
    } catch (error) {
      console.error('Erro ao deletar plano:', error);
      setMensagem('Erro ao deletar plano: ' + error.message);
    }
  };

  const togglePlanStatus = async (plan) => {
    try {
      const planRef = doc(db, 'planos_assinatura', plan.id);
      await updateDoc(planRef, {
        isActive: !plan.isActive,
        updatedAt: Timestamp.now()
      });
      fetchPlans();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setMensagem('Erro ao alterar status do plano');
    }
  };

  const moveOrder = async (plan, direction) => {
    const currentIndex = plans.findIndex(p => p.id === plan.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= plans.length) return;

    const targetPlan = plans[targetIndex];
    
    try {
      await updateDoc(doc(db, 'planos_assinatura', plan.id), {
        displayOrder: targetPlan.displayOrder,
        updatedAt: Timestamp.now()
      });
      
      await updateDoc(doc(db, 'planos_assinatura', targetPlan.id), {
        displayOrder: plan.displayOrder,
        updatedAt: Timestamp.now()
      });
      
      fetchPlans();
    } catch (error) {
      console.error('Erro ao reordenar:', error);
      setMensagem('Erro ao reordenar planos');
    }
  };

  const formatPrice = (price) => {
    return `R$ ${parseFloat(price).toFixed(2).replace('.', ',')}`;
  };

  const getPlanTypeIcon = (type) => {
    switch (type) {
      case 'free': return <FaGift className="type-icon free" />;
      case 'premium': return <FaStar className="type-icon premium" />;
      case 'enterprise': return <FaCrown className="type-icon enterprise" />;
      default: return <FaStar className="type-icon premium" />;
    }
  };

  const getPlanTypeLabel = (type) => {
    switch (type) {
      case 'free': return 'Gratuito';
      case 'premium': return 'Premium';
      case 'enterprise': return 'Enterprise';
      default: return 'Premium';
    }
  };

  return (
    <div className="gerenciar-planos admin-panel-container">
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft className="icon" /> Voltar
        </button>
        <div className="header-title">
          <h1>Gerenciar Planos de Assinatura</h1>
          <p>Sistema completo de gestão de planos - EAD Conect</p>
        </div>
      </header>

      <div className="admin-content">
        {/* Estatísticas */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-number">{plans.length}</div>
            <div className="stat-label">Total de Planos</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{plans.filter(p => p.isActive).length}</div>
            <div className="stat-label">Planos Ativos</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{plans.filter(p => p.isHighlighted).length}</div>
            <div className="stat-label">Em Destaque</div>
          </div>
        </div>

        {/* Formulário de criação */}
        <div className="admin-form-card">
          <h2>
            <FaPlus className="icon" />
            Criar Novo Plano
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nome do Plano *</label>
                <input
                  type="text"
                  id="name"
                  placeholder="Ex: Premium, Enterprise..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="planType">Tipo do Plano *</label>
                <select
                  id="planType"
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                  required
                >
                  <option value="free">Gratuito</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price">Preço Atual (R$) *</label>
                <input
                  type="number"
                  id="price"
                  placeholder="29.90"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  step="0.01"
                  min="0"
                  required={planType !== 'free'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="originalPrice">Preço Original (R$)</label>
                <input
                  type="number"
                  id="originalPrice"
                  placeholder="49.90 (para mostrar desconto)"
                  value={originalPrice}
                  onChange={(e) => setOriginalPrice(e.target.value)}
                  step="0.01"
                  min="0"
                />
                {calculateDiscount() > 0 && (
                  <small className="discount-info">
                    Desconto de {calculateDiscount()}%
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration">Período de Cobrança *</label>
                <select
                  id="duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                >
                  <option value="">Selecione o período</option>
                  <option value="mensal">Mensal</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                  <option value="vitalicio">Vitalício</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="color">Cor do Plano</label>
                <input
                  type="color"
                  id="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição do Plano *</label>
              <textarea
                id="description"
                placeholder="Descreva os benefícios e características do plano"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Recursos/Benefícios do Plano *</label>
              {features.map((feature, index) => (
                <div key={index} className="feature-input-group">
                  <input
                    type="text"
                    placeholder="Ex: Acesso ilimitado a todos os materiais"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    className="feature-input"
                  />
                  {features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="remove-feature-btn"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="add-feature-btn"
              >
                <FaPlus /> Adicionar Recurso
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="displayOrder">Ordem de Exibição</label>
                <input
                  type="number"
                  id="displayOrder"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  min="1"
                />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isHighlighted}
                    onChange={(e) => setIsHighlighted(e.target.checked)}
                  />
                  <span>Plano em destaque</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>Plano ativo</span>
                </label>
              </div>
            </div>

            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? (
                <>
                  <FaSpinner className="icon spinning" />
                  Criando Plano...
                </>
              ) : (
                <>
                  <FaPlus className="icon" />
                  Criar Plano
                </>
              )}
            </button>
          </form>
        </div>

        {mensagem && (
          <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>
            {mensagem}
          </div>
        )}

        {/* Lista de planos */}
        <div className="admin-list">
          <h2>Planos Cadastrados ({plans.length})</h2>
          {plans.length === 0 ? (
            <div className="empty-state">
              <FaStar className="empty-icon" />
              <h3>Nenhum plano cadastrado</h3>
              <p>Crie seu primeiro plano de assinatura acima</p>
            </div>
          ) : (
            <div className="plans-list">
              {plans.map((plan, index) => (
                <div 
                  key={plan.id} 
                  className={`plan-item ${!plan.isActive ? 'inactive' : ''} ${plan.isHighlighted ? 'highlighted' : ''}`}
                >
                  <div className="plan-header">
                    <div className="plan-info">
                      <div className="plan-title">
                        {getPlanTypeIcon(plan.planType)}
                        <h3>{plan.name}</h3>
                        {plan.isHighlighted && <FaStar className="highlight-badge" />}
                      </div>
                      <div className="plan-pricing">
                        {plan.originalPrice && (
                          <span className="original-price">{formatPrice(plan.originalPrice)}</span>
                        )}
                        <span className="current-price">{formatPrice(plan.price)}</span>
                        <span className="period">/{plan.duration}</span>
                        {plan.discount > 0 && (
                          <span className="discount-badge">-{plan.discount}%</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="plan-actions">
                      <div className="order-controls">
                        <button
                          onClick={() => moveOrder(plan, 'up')}
                          disabled={index === 0}
                          className="order-btn"
                          title="Mover para cima"
                        >
                          <FaArrowUp />
                        </button>
                        <span className="order-number">{plan.displayOrder}</span>
                        <button
                          onClick={() => moveOrder(plan, 'down')}
                          disabled={index === plans.length - 1}
                          className="order-btn"
                          title="Mover para baixo"
                        >
                          <FaArrowDown />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => togglePlanStatus(plan)}
                        className={`status-btn ${plan.isActive ? 'active' : 'inactive'}`}
                        title={plan.isActive ? 'Desativar' : 'Ativar'}
                      >
                        {plan.isActive ? <FaEye /> : <FaEyeSlash />}
                      </button>
                      
                      <button
                        onClick={() => openEditModal(plan)}
                        className="edit-btn"
                        title="Editar"
                      >
                        <FaEdit />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(plan.id)}
                        className="delete-btn"
                        title="Deletar"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  
                  <div className="plan-details">
                    <p className="plan-description">{plan.description}</p>
                    <div className="plan-features">
                      {plan.features?.map((feature, idx) => (
                        <div key={idx} className="feature-item">
                          <FaCheck className="check-icon" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="plan-meta">
                      <span className="plan-type-badge" style={{ backgroundColor: plan.color }}>
                        {getPlanTypeLabel(plan.planType)}
                      </span>
                      <span className="plan-date">
                        Criado em {plan.createdAt?.toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de edição */}
      {editModalOpen && editPlan && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Plano: {editPlan.name}</h2>
              <button
                onClick={() => setEditModalOpen(false)}
                className="modal-close"
              >
                <FaTimes />
              </button>
            </div>
            
            {/* Conteúdo similar ao formulário principal, mas com editPlan */}
            <form onSubmit={handleEditSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome do Plano *</label>
                  <input
                    type="text"
                    value={editPlan.name}
                    onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Tipo do Plano *</label>
                  <select
                    value={editPlan.planType}
                    onChange={(e) => setEditPlan({ ...editPlan, planType: e.target.value })}
                    required
                  >
                    <option value="free">Gratuito</option>
                    <option value="premium">Premium</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Preço Atual (R$) *</label>
                  <input
                    type="number"
                    value={editPlan.price}
                    onChange={(e) => setEditPlan({ ...editPlan, price: e.target.value })}
                    step="0.01"
                    min="0"
                    required={editPlan.planType !== 'free'}
                  />
                </div>
                <div className="form-group">
                  <label>Preço Original (R$)</label>
                  <input
                    type="number"
                    value={editPlan.originalPrice}
                    onChange={(e) => setEditPlan({ ...editPlan, originalPrice: e.target.value })}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Período *</label>
                  <select
                    value={editPlan.duration}
                    onChange={(e) => setEditPlan({ ...editPlan, duration: e.target.value })}
                    required
                  >
                    <option value="mensal">Mensal</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                    <option value="vitalicio">Vitalício</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Cor do Plano</label>
                  <input
                    type="color"
                    value={editPlan.color}
                    onChange={(e) => setEditPlan({ ...editPlan, color: e.target.value })}
                    className="color-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Descrição *</label>
                <textarea
                  value={editPlan.description}
                  onChange={(e) => setEditPlan({ ...editPlan, description: e.target.value })}
                  required
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Recursos do Plano *</label>
                {editPlan.features?.map((feature, index) => (
                  <div key={index} className="feature-input-group">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...editPlan.features];
                        newFeatures[index] = e.target.value;
                        setEditPlan({ ...editPlan, features: newFeatures });
                      }}
                      className="feature-input"
                    />
                    {editPlan.features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newFeatures = editPlan.features.filter((_, i) => i !== index);
                          setEditPlan({ ...editPlan, features: newFeatures });
                        }}
                        className="remove-feature-btn"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setEditPlan({ ...editPlan, features: [...editPlan.features, ''] });
                  }}
                  className="add-feature-btn"
                >
                  <FaPlus /> Adicionar Recurso
                </button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ordem de Exibição</label>
                  <input
                    type="number"
                    value={editPlan.displayOrder}
                    onChange={(e) => setEditPlan({ ...editPlan, displayOrder: e.target.value })}
                    min="1"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editPlan.isHighlighted}
                      onChange={(e) => setEditPlan({ ...editPlan, isHighlighted: e.target.checked })}
                    />
                    <span>Plano em destaque</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editPlan.isActive}
                      onChange={(e) => setEditPlan({ ...editPlan, isActive: e.target.checked })}
                    />
                    <span>Plano ativo</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="cancelar"
                >
                  <FaTimes /> Cancelar
                </button>
                <button type="submit" className="salvar" disabled={loading}>
                  {loading ? (
                    <>
                      <FaSpinner className="spinning" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarPlanos;